import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../apiConfig';
import { sanitizeHTML } from '../../utils/security';
import './SocialComponents.css';

const CommentSection = ({ 
  itemId, 
  itemType, 
  initialCount = 0,
  isExpanded = false,
  onToggle,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [expanded, setExpanded] = useState(isExpanded);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(initialCount);
  const textareaRef = useRef(null);

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount]);

  useEffect(() => {
    if (expanded && comments.length === 0) {
      loadComments();
    }
  }, [expanded]);

  // Real-time updates via Socket.io
  useEffect(() => {
    if (window.socket) {
      const handleCommentAdded = (data) => {
        if (data.itemId === itemId && data.itemType === itemType) {
          setComments(prev => [data.comment, ...prev]);
          setCount(data.commentsCount);
        }
      };

      const handleCommentUpdated = (data) => {
        if (data.itemId === itemId && data.itemType === itemType) {
          setComments(prev => prev.map(comment => 
            comment.id === data.comment.id ? data.comment : comment
          ));
        }
      };

      const handleCommentDeleted = (data) => {
        if (data.itemId === itemId && data.itemType === itemType) {
          setComments(prev => prev.filter(comment => comment.id !== data.commentId));
          setCount(data.commentsCount);
        }
      };

      window.socket.on('comment_added', handleCommentAdded);
      window.socket.on('comment_updated', handleCommentUpdated);
      window.socket.on('comment_deleted', handleCommentDeleted);
      
      return () => {
        window.socket.off('comment_added', handleCommentAdded);
        window.socket.off('comment_updated', handleCommentUpdated);
        window.socket.off('comment_deleted', handleCommentDeleted);
      };
    }
  }, [itemId, itemType]);

  const loadComments = async (pageNum = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/social/comments/${itemType}/${itemId}?page=${pageNum}`);
      
      if (pageNum === 1) {
        setComments(response.data.comments);
      } else {
        setComments(prev => [...prev, ...response.data.comments]);
      }
      
      setHasMore(response.data.hasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (onToggle) {
      onToggle(newExpanded);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !newComment.trim() || submitting) {
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await api.post('/social/comment', {
        itemId,
        itemType,
        content: newComment.trim()
      });

      setNewComment('');
      setCount(response.data.commentsCount);
      
      // Add comment to list if comments are expanded
      if (expanded) {
        setComments(prev => [response.data.comment, ...prev]);
      }

      // Emit Socket.io event
      if (window.socket) {
        window.socket.emit('comment_added', {
          itemId,
          itemType,
          comment: response.data.comment,
          commentsCount: response.data.commentsCount
        });
      }

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTextareaChange = (e) => {
    setNewComment(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const loadMoreComments = () => {
    if (hasMore && !loading) {
      loadComments(page + 1);
    }
  };

  return (
    <div className={`comment-section ${className}`}>
      {/* Toggle button */}
      <button
        className="comment-section__toggle"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide comments' : 'Show comments'}
      >
        <span className="comment-section__icon" aria-hidden="true">💬</span>
        <span className="comment-section__count">
          {count > 0 ? `${count} comment${count !== 1 ? 's' : ''}` : 'Comment'}
        </span>
        <span className="comment-section__chevron" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded comments */}
      {expanded && (
        <div className="comment-section__content">
          {/* Comment form */}
          {currentUser && (
            <form className="comment-form" onSubmit={handleSubmit}>
              <div className="comment-form__avatar">
                {currentUser.avatar_url ? (
                  <img 
                    src={currentUser.avatar_url} 
                    alt={currentUser.name}
                    className="comment-form__avatar-img"
                  />
                ) : (
                  <div className="comment-form__avatar-placeholder">
                    {currentUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>
              <div className="comment-form__input-container">
                <textarea
                  ref={textareaRef}
                  className="comment-form__textarea"
                  placeholder="Write a comment... (supports **bold** and *italic* markdown)"
                  value={newComment}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  maxLength={1000}
                  disabled={submitting}
                  aria-label="Write a comment"
                />
                <div className="comment-form__actions">
                  <div className="comment-form__help">
                    <span>Cmd/Ctrl + Enter to post</span>
                  </div>
                  <button
                    type="submit"
                    className="comment-form__submit"
                    disabled={!newComment.trim() || submitting}
                    aria-label="Post comment"
                  >
                    {submitting ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Comments list */}
          <div className="comment-list">
            {loading && comments.length === 0 ? (
              <div className="comment-list__loading">
                <div className="comment-list__spinner"></div>
                <span>Loading comments...</span>
              </div>
            ) : comments.length > 0 ? (
              <>
                {comments.map(comment => (
                  <Comment 
                    key={comment.id} 
                    comment={comment} 
                    currentUser={currentUser}
                    itemId={itemId}
                    itemType={itemType}
                  />
                ))}
                
                {hasMore && (
                  <button
                    className="comment-list__load-more"
                    onClick={loadMoreComments}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load more comments'}
                  </button>
                )}
              </>
            ) : (
              <div className="comment-list__empty">
                <span>No comments yet. Be the first to comment!</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Comment Component
const Comment = ({ comment, currentUser, itemId, itemType }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEdit = currentUser && currentUser.id === comment.user_id;

  const handleEdit = async () => {
    if (!editContent.trim() || updating) return;

    setUpdating(true);
    try {
      await api.put(`/social/comment/${comment.id}`, {
        content: editContent.trim()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/social/comment/${comment.id}`);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`comment ${deleting ? 'comment--deleting' : ''}`}>
      <div className="comment__avatar">
        {comment.user_avatar ? (
          <img 
            src={comment.user_avatar} 
            alt={comment.user_name}
            className="comment__avatar-img"
          />
        ) : (
          <div className="comment__avatar-placeholder">
            {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      
      <div className="comment__content">
        <div className="comment__header">
          <span className="comment__author">{comment.user_name}</span>
          <time className="comment__time" dateTime={comment.created_at}>
            {formatDate(comment.created_at)}
          </time>
          {comment.is_edited && (
            <span className="comment__edited">(edited)</span>
          )}
        </div>
        
        <div className="comment__body">
          {isEditing ? (
            <div className="comment__edit-form">
              <textarea
                className="comment__edit-textarea"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                maxLength={1000}
                disabled={updating}
              />
              <div className="comment__edit-actions">
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={updating}
                  className="comment__edit-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEdit}
                  disabled={!editContent.trim() || updating}
                  className="comment__edit-save"
                >
                  {updating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div 
              className="comment__text" 
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(comment.content) }}
            />
          )}
        </div>
        
        {canEdit && !isEditing && (
          <div className="comment__actions">
            <button
              onClick={() => setIsEditing(true)}
              className="comment__action-button"
              disabled={deleting}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="comment__action-button comment__action-button--danger"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection; 